import { CommentaryEntityRef, CommentaryEntityType } from '../types/thread-jump'

export type FollowNoticeReason =
  | 'notFound'
  | 'loadFailed'
  /** Nothing matched, but the session's data was still loading. */
  | 'stillLoading'
  /** The stored id can't be matched by the grid's id column. */
  | 'olderReference'
  | 'breakOutsideSearch'
  /** The break isn't loaded and asking the server about it failed. */
  | 'breakNotLoaded'

export interface FollowNotice {
  title: string
  message: string
}

const NOUNS: Record<CommentaryEntityType, string> = {
  account: 'Account',
  transaction: 'Transaction',
  externalTransaction: 'External Transaction',
  break: 'Break',
  asset: 'Asset',
  suspense: 'Suspense',
}

export function followNotice(
  target: CommentaryEntityRef,
  reason: FollowNoticeReason,
): FollowNotice {
  const noun = NOUNS[target.type]
  // The label crosses from another bundle in a DOM event, so its type isn't guaranteed.
  const label = typeof target.label === 'string' ? target.label.trim() : ''
  const name = label || `${noun} ${target.id}`
  switch (reason) {
    case 'loadFailed':
      return {
        title: `${noun} not loaded`,
        message: `${name} could not be loaded. Your filters have been restored.`,
      }
    case 'stillLoading':
      return {
        title: `${noun} not loaded yet`,
        message: `${name} isn't loaded yet. This session's data is still loading, so try again when it finishes.`,
      }
    case 'olderReference':
      return {
        title: `${noun} not found`,
        message: `${name} uses an older transaction reference and can't be opened here.`,
      }
    case 'breakOutsideSearch':
      return {
        title: 'Break not loaded',
        message: `${name} is open, but isn't in the breaks loaded here. Search for it to view it.`,
      }
    case 'breakNotLoaded':
      return {
        title: 'Break not loaded',
        message: `${name} isn't in the breaks loaded here.`,
      }
    case 'notFound':
      return {
        title: `${noun} not found`,
        message: notFoundMessage(target, name),
      }
  }
}

function notFoundMessage(target: CommentaryEntityRef, name: string): string {
  switch (target.type) {
    case 'transaction':
      return `${name} could not be found in this session. It may have been deleted or fall outside the loaded date range.`
    case 'break':
      return `${name} is no longer open.`
    default:
      return `${name} could not be found in this session.`
  }
}
